import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useIntercom } from 'react-use-intercom';

import { PremiumTestPrepPlaywallModal } from '../../components/_molecules/PremiumTestPrepPlaywallModal/PremiumTestPrepPlaywallModal';
import { Slide } from '../../components/SlideComponents/slide.types';
import useAccessBadge, { LicenseCoarseLevels } from '../../hooks/useAccessBadge';
import useAttioEventTracking from '../../hooks/useAttioEventTracking';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useMetrics } from '../../hooks/useMetrics';
import useTeachingCreditState from '../../hooks/useTeachingCreditState';
import { isPremiumTestPrepPresentation } from '../../utils/premiumContentDetection';

const PREMIUM_TEST_PREP_LIMIT = 3;

interface PremiumTestPrepPlaywallContextType {
  checkAndPromptPremiumTestPrepPaywall: (
    slides: Slide[],
    source: string,
    onConfirm: () => void,
  ) => boolean;
  shouldShowPremiumIndicator: (slides: Slide[]) => boolean;
  isPremiumTestPrepPaywallVisible: boolean;
  isFeatureEnabled: boolean;
  isNotEnterprise: boolean;
  premiumTestPrepRemaining: number;
  premiumTestPrepHasReachedLimit: boolean;
}

const PremiumTestPrepPlaywallContext = createContext<
  PremiumTestPrepPlaywallContextType | undefined
>(undefined);

export const PremiumTestPrepPlaywallProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const isFeatureEnabled = useFeatureFlag('premium-testprep-play-limit') ?? false;
  const { accessBadge: userAccessBadge, isFetched } = useAccessBadge();
  const { creditState } = useTeachingCreditState();
  const metrics = useMetrics();
  const { trackEvent } = useIntercom();
  const trackAttioEvent = useAttioEventTracking();

  const [isPaywallVisible, setIsPaywallVisible] = useState(false);
  const [pendingOnConfirm, setPendingOnConfirm] = useState<(() => void) | null>(null);
  const [currentTestName, setCurrentTestName] = useState<string | undefined>();

  const isNotEnterprise = useMemo(() => {
    return (
      isFetched &&
      userAccessBadge?.license?.coarseLevel !== LicenseCoarseLevels.enterprise
    );
  }, [isFetched, userAccessBadge]);

  const premiumTestPrepPlayed = creditState?.premiumTestPrepPlayed ?? 0;
  const premiumTestPrepLimit =
    creditState?.premiumTestPrepLimit ?? PREMIUM_TEST_PREP_LIMIT;
  const premiumTestPrepRemaining = Math.max(
    0,
    premiumTestPrepLimit - premiumTestPrepPlayed,
  );
  const premiumTestPrepHasReachedLimit = premiumTestPrepRemaining <= 0;

  const shouldShowPremiumIndicator = useCallback(
    (slides: Slide[]) => {
      if (!isFeatureEnabled || !isFetched) return false;
      return isPremiumTestPrepPresentation(slides);
    },
    [isFeatureEnabled, isFetched],
  );

  const checkAndPromptPremiumTestPrepPaywall: PremiumTestPrepPlaywallContextType['checkAndPromptPremiumTestPrepPaywall'] = useCallback(
    (slides: Slide[], source: string, onConfirm: () => void): boolean => {
      if (!isFeatureEnabled) {
        return false;
      }

      if (!isNotEnterprise) {
        return false;
      }

      const isPremiumTestPrep = isPremiumTestPrepPresentation(slides);
      if (!isPremiumTestPrep) {
        return false;
      }

      const eventProps = {
        source,
        premiumTestPrepPlayed,
        premiumTestPrepRemaining,
        premiumTestPrepLimit,
        hasReachedLimit: premiumTestPrepHasReachedLimit,
        plan: userAccessBadge?.license?.coarseLevel,
        subPlan: userAccessBadge?.license?.fineLevel,
        organizationId: userAccessBadge?.organization?.id,
      };

      if (premiumTestPrepHasReachedLimit) {
        metrics.logEvent('PremiumTestPrep.StartBlocked', eventProps);
        trackEvent('premium_testprep_start_blocked', eventProps);
        trackAttioEvent('premium_testprep_blocked', {
          organizationId: userAccessBadge?.organization?.id,
          userRole: userAccessBadge?.user?.profileInfo?.role,
          email: userAccessBadge?.user?.email,
          firstName: userAccessBadge?.user?.firstName,
          lastName: userAccessBadge?.user?.lastName,
          source,
        });
        setPendingOnConfirm(null);
      } else {
        metrics.logEvent('PremiumTestPrep.BannerShown', eventProps);
        setPendingOnConfirm(() => onConfirm);
      }

      setIsPaywallVisible(true);
      return true;
    },
    [
      isFeatureEnabled,
      isNotEnterprise,
      premiumTestPrepPlayed,
      premiumTestPrepRemaining,
      premiumTestPrepLimit,
      premiumTestPrepHasReachedLimit,
      userAccessBadge,
      metrics,
      trackEvent,
      trackAttioEvent,
    ],
  );

  const handleClose = useCallback(() => {
    metrics.logEvent('PremiumTestPrep.PaywallDismissed', {
      hasReachedLimit: premiumTestPrepHasReachedLimit,
      remaining: premiumTestPrepRemaining,
      plan: userAccessBadge?.license?.coarseLevel,
      organizationId: userAccessBadge?.organization?.id,
    });
    setIsPaywallVisible(false);
    setPendingOnConfirm(null);
  }, [
    metrics,
    premiumTestPrepHasReachedLimit,
    premiumTestPrepRemaining,
    userAccessBadge,
  ]);

  const handleConfirm = useCallback(() => {
    setIsPaywallVisible(false);
    if (pendingOnConfirm) {
      pendingOnConfirm();
    }
    setPendingOnConfirm(null);
  }, [pendingOnConfirm]);

  const contextValue = useMemo<PremiumTestPrepPlaywallContextType>(
    () => ({
      checkAndPromptPremiumTestPrepPaywall,
      shouldShowPremiumIndicator,
      isPremiumTestPrepPaywallVisible: isPaywallVisible,
      isFeatureEnabled,
      isNotEnterprise,
      premiumTestPrepRemaining,
      premiumTestPrepHasReachedLimit,
    }),
    [
      checkAndPromptPremiumTestPrepPaywall,
      shouldShowPremiumIndicator,
      isPaywallVisible,
      isFeatureEnabled,
      isNotEnterprise,
      premiumTestPrepRemaining,
      premiumTestPrepHasReachedLimit,
    ],
  );

  return (
    <PremiumTestPrepPlaywallContext.Provider value={contextValue}>
      {children}
      <PremiumTestPrepPlaywallModal
        visible={isPaywallVisible}
        onClose={handleClose}
        onConfirm={!premiumTestPrepHasReachedLimit ? handleConfirm : undefined}
        remaining={premiumTestPrepRemaining}
        total={premiumTestPrepLimit}
        region={userAccessBadge?.organization?.region}
        country={userAccessBadge?.organization?.country}
        testName={currentTestName}
      />
    </PremiumTestPrepPlaywallContext.Provider>
  );
};

const defaultContext: PremiumTestPrepPlaywallContextType = {
  checkAndPromptPremiumTestPrepPaywall: () => false,
  shouldShowPremiumIndicator: () => false,
  isPremiumTestPrepPaywallVisible: false,
  isFeatureEnabled: false,
  isNotEnterprise: false,
  premiumTestPrepRemaining: 0,
  premiumTestPrepHasReachedLimit: false,
};

export const usePremiumTestPrepPlaywall = (): PremiumTestPrepPlaywallContextType => {
  const context = useContext(PremiumTestPrepPlaywallContext);
  return context ?? defaultContext;
};
